import type { AppEnvSchema } from '@ingenico/util-mfe-shell/env';
import { isEqual } from 'lodash-es';
import { z } from 'zod';

export type SupportedEnvSource = Record<string, string | undefined>;

const getEnvString = (envSource: SupportedEnvSource, key: string, fallbackValue = ''): string =>
  envSource[key] || fallbackValue;

const getEnvBoolean = (envSource: SupportedEnvSource, key: string): boolean =>
  isEqual(envSource[key]?.toLowerCase(), 'true');

export enum ProvisioningEnvKey {
  GmapKey = 'NX_PUBLIC_GMAP_KEY',
  GmapApi = 'NX_PUBLIC_GMAP_API',
  GmapUrl = 'NX_PUBLIC_GMAP_URL',
  TemBaseUrl = 'NX_PUBLIC_TEM_BASE_URL',
  EnableSyncConfiguration = 'NX_PUBLIC_ENABLE_SYNC_CONFIGURATION',
  EnableDeviceDiagnosticsFeature = 'NX_PUBLIC_ENABLE_DEVICE_DIAGNOSTICS_FEATURE',
  EnableViewOutputFeature = 'NX_PUBLIC_ENABLE_VIEW_OUTPUT',
  EnableGeofencing = 'NX_PUBLIC_ENABLE_GEOFENCING',
  EnableArchivedDeviceFeature = 'NX_PUBLIC_ENABLE_ARCHIVED_DEVICE_FEATURE',
  EnableVirtualDeviceSuspendResume = 'NX_PUBLIC_ENABLE_VIRTUAL_DEVICE_SUSPEND_RESUME',
  EnableMerchantLifecycle = 'NX_PUBLIC_ENABLE_MERCHANT_LIFECYCLE',
  EnableMsSyncConfiguration = 'NX_PUBLIC_ENABLE_MS_SYNC_CONFIGURATION',
}

export enum DeviceManagementEnvKey {
  EnableTemplateParametersOptions = 'NX_PUBLIC_ENABLE_TEMPLATE_PARAMETERS_OPTIONS',
  EnableDownloadRules = 'NX_PUBLIC_ENABLE_DOWNLOAD_RULES',
  EnableSmartfieldCategoryFeature = 'NX_PUBLIC_ENABLE_SMARTFIELD_CATEGORY_FEATURE',
  EnableServicesFeature = 'NX_PUBLIC_ENABLE_SERVICES_FEATURE',
  EnableDeviceBlock = 'NX_PUBLIC_ENABLE_DEVICE_BLOCK',
  EnableDeviceLog = 'NX_PUBLIC_ENABLE_DEVICE_LOG',
  EnableAdl = 'NX_PUBLIC_ENABLE_ADL',
  EnableParametersImportExport = 'NX_PUBLIC_ENABLE_PARAMETERS_IMPORT_EXPORT',
  EnableSmartfieldValuesComputedConfiguration = 'NX_PUBLIC_ENABLE_SMARTFIELD_VALUES_COMPUTED_CONFIGURATION',
  EnableStoreSmartFieldFeature = 'NX_PUBLIC_ENABLE_STORE_SMARTFIELD_FEATURE',
  EnableTagSmartFieldFeature = 'NX_PUBLIC_ENABLE_SMARTFIELD_TAG_TAB',
  EnableSuborgNameVisibility = 'NX_PUBLIC_ENABLE_SUBORG_NAME_VISIBILITY',
  EnableFormBuilderDynamicChoice = 'NX_PUBLIC_ENABLE_FORM_BUILDER_DC',
  EnableFormBuilderSkipDefaultTemplateValues = 'NX_PUBLIC_ENABLE_FORM_BUILDER_SKIP_DEFAULT_TEMPLATE_VALUES',
  EnableKeysetSegregation = 'NX_PUBLIC_ENABLE_KEYSET_SEGREGATION',
  EnableProgressiveDeployment = 'NX_PUBLIC_ENABLE_PROGRESSIVE_DEPLOYMENT',
  EnableAppLinkDelivery = 'NX_PUBLIC_ENABLE_APP_LINK_DELIVERY',
  EnableFileAsConfig = 'NX_PUBLIC_ENABLE_FILE_AS_CONFIG',
  EnableConfigProfileOrdering = 'NX_PUBLIC_ENABLE_CONFIG_PROFILE_ORDERING',
  EnableDmMultiMerchant = 'NX_PUBLIC_ENABLE_DM_MULTI_MERCHANT',
  EnableConfigProfilePut = 'NX_PUBLIC_ENABLE_CONFIG_PROFILE_PUT',
  EnableSkipBlockedDeployment = 'NX_PUBLIC_ENABLE_SKIP_BLOCKED_DEPLOYMENT',
}

export enum HardwareManagementEnvKey {
  EnableHmsRequests = 'NX_PUBLIC_ENABLE_HMS_REQUESTS',
  EnableHmsInventory = 'NX_PUBLIC_ENABLE_HMS_INVENTORY',
}

export enum TransactionManagementEnvKey {
  EnableTransactionsFeature = 'NX_PUBLIC_ENABLE_TRANSACTIONS_FEATURE',
}

export enum DigitalReceiptEnvKey {
  EnableDigitalReceiptFeature = 'NX_PUBLIC_ENABLE_DIGITAL_RECEIPT',
  EnableDigitalReceiptV2 = 'NX_PUBLIC_ENABLE_DIGITAL_RECEIPT_V2',
}

export enum UserManagementEnvKey {
  EnableCustomRoleFeature = 'NX_PUBLIC_ENABLE_CUSTOM_ROLE_FEATURE',
  EnableUserDetailsEsignFeature = 'NX_PUBLIC_ENABLE_USER_DETAILS_ESIGN_FEATURE',
  EnableUserResetPasswordFeature = 'NX_PUBLIC_ENABLE_RESET_PASSWORD_OPTION',
  EnableIdpSourceUserFeature = 'NX_PUBLIC_ENABLE_USER_TYPE_VISIBILITY',
  EnableClientServiceAccountFeature = 'NX_PUBLIC_ENABLE_CLIENT_SERVICE_ACCOUNT',
}

export enum ReportingEnvKey {
  EnableReportBuilderV2 = 'NX_PUBLIC_ENABLE_REPORT_BUILDER_V2',
}

export enum SecurityEnvKey {
  EnableDocumentsFeature = 'NX_PUBLIC_ENABLE_DOCUMENTS_FEATURE',
}

export enum EsignEnvKey {
  EnableEsignSigningKeys = 'NX_PUBLIC_ENABLE_ESIGN_SIGNING_KEYS_FEATURE',
  EnableEsignSignatures = 'NX_PUBLIC_ENABLE_ESIGN_SIGNATURES_FEATURE',
  EnableEsignUsers = 'NX_PUBLIC_ENABLE_ESIGN_USERS_FEATURE',
}

export enum NotificationsEnvKey {
  EnableWebhookFeature = 'NX_PUBLIC_ENABLE_WEBHOOK_FEATURE',
}

export enum SubscriptionEnvKey {
  ReadyServices = 'NX_PUBLIC_READY_SERVICES',
}

export const createEnvironmentVariablesSchema = (envSource: SupportedEnvSource = process.env): AppEnvSchema => ({
  SHOW_RELEASE_NOTES: {
    schema: z.boolean(),
    value: true,
  },
  [ProvisioningEnvKey.GmapKey]: {
    schema: z.string(),
    value: getEnvString(envSource, ProvisioningEnvKey.GmapKey),
  },
  [ProvisioningEnvKey.GmapApi]: {
    schema: z
      .string()
      .default('https://maps.googleapis.com/maps/api/staticmap')
      .describe('API to get the image of the location'),
    value: getEnvString(envSource, ProvisioningEnvKey.GmapApi, 'https://maps.googleapis.com/maps/api/staticmap'),
  },
  [ProvisioningEnvKey.GmapUrl]: {
    schema: z.string().default('https://www.google.com/maps/place').describe('URL to redirect to google maps website'),
    value: getEnvString(envSource, ProvisioningEnvKey.GmapUrl, 'https://www.google.com/maps/place'),
  },
  [ProvisioningEnvKey.TemBaseUrl]: {
    schema: z.string(),
    value: getEnvString(envSource, ProvisioningEnvKey.TemBaseUrl),
  },
  [ProvisioningEnvKey.EnableSyncConfiguration]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, ProvisioningEnvKey.EnableSyncConfiguration),
  },
  [ProvisioningEnvKey.EnableDeviceDiagnosticsFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, ProvisioningEnvKey.EnableDeviceDiagnosticsFeature),
  },
  [DeviceManagementEnvKey.EnableTemplateParametersOptions]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableTemplateParametersOptions),
  },
  [DeviceManagementEnvKey.EnableDownloadRules]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableDownloadRules),
  },
  [DeviceManagementEnvKey.EnableSmartfieldCategoryFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableSmartfieldCategoryFeature),
  },
  [DeviceManagementEnvKey.EnableServicesFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableServicesFeature),
  },
  [TransactionManagementEnvKey.EnableTransactionsFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, TransactionManagementEnvKey.EnableTransactionsFeature),
  },
  [DigitalReceiptEnvKey.EnableDigitalReceiptFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DigitalReceiptEnvKey.EnableDigitalReceiptFeature),
  },
  [DigitalReceiptEnvKey.EnableDigitalReceiptV2]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DigitalReceiptEnvKey.EnableDigitalReceiptV2),
  },
  [UserManagementEnvKey.EnableCustomRoleFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, UserManagementEnvKey.EnableCustomRoleFeature),
  },
  [UserManagementEnvKey.EnableUserResetPasswordFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, UserManagementEnvKey.EnableUserResetPasswordFeature),
  },
  [ReportingEnvKey.EnableReportBuilderV2]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, ReportingEnvKey.EnableReportBuilderV2),
  },
  [UserManagementEnvKey.EnableUserDetailsEsignFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, UserManagementEnvKey.EnableUserDetailsEsignFeature),
  },
  [DeviceManagementEnvKey.EnableDeviceBlock]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableDeviceBlock),
  },
  [DeviceManagementEnvKey.EnableDeviceLog]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableDeviceLog),
  },
  [DeviceManagementEnvKey.EnableAdl]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableAdl),
  },
  [DeviceManagementEnvKey.EnableParametersImportExport]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableParametersImportExport),
  },
  [DeviceManagementEnvKey.EnableSmartfieldValuesComputedConfiguration]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableSmartfieldValuesComputedConfiguration),
  },
  [DeviceManagementEnvKey.EnableStoreSmartFieldFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableStoreSmartFieldFeature),
  },
  [DeviceManagementEnvKey.EnableTagSmartFieldFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableTagSmartFieldFeature),
  },
  [HardwareManagementEnvKey.EnableHmsInventory]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, HardwareManagementEnvKey.EnableHmsInventory),
  },
  [HardwareManagementEnvKey.EnableHmsRequests]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, HardwareManagementEnvKey.EnableHmsRequests),
  },
  [SecurityEnvKey.EnableDocumentsFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, SecurityEnvKey.EnableDocumentsFeature),
  },
  [ProvisioningEnvKey.EnableViewOutputFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, ProvisioningEnvKey.EnableViewOutputFeature),
  },
  [EsignEnvKey.EnableEsignSigningKeys]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, EsignEnvKey.EnableEsignSigningKeys),
  },
  [EsignEnvKey.EnableEsignSignatures]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, EsignEnvKey.EnableEsignSignatures),
  },
  [EsignEnvKey.EnableEsignUsers]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, EsignEnvKey.EnableEsignUsers),
  },
  [SubscriptionEnvKey.ReadyServices]: {
    schema: z.string(),
    value: getEnvString(envSource, SubscriptionEnvKey.ReadyServices),
  },
  [ProvisioningEnvKey.EnableGeofencing]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, ProvisioningEnvKey.EnableGeofencing),
  },
  [ProvisioningEnvKey.EnableVirtualDeviceSuspendResume]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, ProvisioningEnvKey.EnableVirtualDeviceSuspendResume),
  },
  [ProvisioningEnvKey.EnableMerchantLifecycle]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, ProvisioningEnvKey.EnableMerchantLifecycle),
  },
  [UserManagementEnvKey.EnableIdpSourceUserFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, UserManagementEnvKey.EnableIdpSourceUserFeature),
  },
  [NotificationsEnvKey.EnableWebhookFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, NotificationsEnvKey.EnableWebhookFeature),
  },
  [DeviceManagementEnvKey.EnableSuborgNameVisibility]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableSuborgNameVisibility),
  },
  [DeviceManagementEnvKey.EnableFormBuilderDynamicChoice]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableFormBuilderDynamicChoice),
  },
  [DeviceManagementEnvKey.EnableFormBuilderSkipDefaultTemplateValues]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableFormBuilderSkipDefaultTemplateValues),
  },
  [DeviceManagementEnvKey.EnableKeysetSegregation]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableKeysetSegregation),
  },
  [ProvisioningEnvKey.EnableArchivedDeviceFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, ProvisioningEnvKey.EnableArchivedDeviceFeature),
  },
  [UserManagementEnvKey.EnableClientServiceAccountFeature]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, UserManagementEnvKey.EnableClientServiceAccountFeature),
  },
  [DeviceManagementEnvKey.EnableProgressiveDeployment]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableProgressiveDeployment),
  },
  [DeviceManagementEnvKey.EnableSkipBlockedDeployment]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableSkipBlockedDeployment),
  },
  [DeviceManagementEnvKey.EnableAppLinkDelivery]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableAppLinkDelivery),
  },
  [DeviceManagementEnvKey.EnableFileAsConfig]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableFileAsConfig),
  },
  [DeviceManagementEnvKey.EnableConfigProfileOrdering]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableConfigProfileOrdering),
  },
  [DeviceManagementEnvKey.EnableDmMultiMerchant]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableDmMultiMerchant),
  },
  [DeviceManagementEnvKey.EnableConfigProfilePut]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, DeviceManagementEnvKey.EnableConfigProfilePut),
  },
  [ProvisioningEnvKey.EnableMsSyncConfiguration]: {
    schema: z.boolean(),
    value: getEnvBoolean(envSource, ProvisioningEnvKey.EnableMsSyncConfiguration),
  },
});

export const environmentVariablesSchema: AppEnvSchema = createEnvironmentVariablesSchema();

export type DeviceManagementEnvDataAndSchemas = typeof environmentVariablesSchema;
